import { MapInfoPolygon, Polygon, Position } from 'src/common-services'
import { atom, DefaultValue } from 'recoil'
import { DEFAULT_LRU_SIZE, keyFactory, localStorageEffect, uncachedSelector } from 'src/utils/recoil'
import { Bounds, MainMapLayers, MapStyle, MapTypeId } from 'src/types/map'
import { toMultiPolygon, boundsToPolygon, getLastLocation, lastLocationKey, austin } from 'src/utils/map'
import { MAP_DEFAULT_STYLE } from 'src/components/map/mapbox/config'

const key = keyFactory('map')

export const bounds = atom<Bounds>({
  key: key('bounds'),
  default: getLastLocation(),
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((newVal: Bounds | DefaultValue) => {
        if (newVal instanceof DefaultValue) {
          return
        }
        localStorage.setItem(lastLocationKey, JSON.stringify(newVal))
      })
    },
  ],
})

export const showPOIs = atom<boolean>({
  key: key('showPOIs'),
  default: true,
  effects_UNSTABLE: [localStorageEffect('show-google-pois')],
})

export const mainMapCenter = uncachedSelector<Position>({
  key: key('main-map-center'),
  get: ({ get }) => {
    return get(bounds).center
  },
  lru: DEFAULT_LRU_SIZE,
})

export const zoom = atom<number>({
  key: key('zoom'),
  default: 14,
  effects_UNSTABLE: [localStorageEffect('map-zoom')],
})

export const mapType = atom<MapTypeId>({
  key: key('map-type-id'),
  default: 'hybrid',
  effects: [localStorageEffect('map-type')],
})

export const isAerialEnabled = atom<boolean>({
  key: key('is-aerial-enabled'),
  default: false,
})

export const streetView = atom<boolean>({
  key: key('street-view'),
  default: false,
})

// turns the google maps bounding box into a geoJSON polygon
export const polygon = uncachedSelector<Polygon>({
  key: key('polygon'),
  get: ({ get }) => {
    const mapBounds = get(bounds)
    return boundsToPolygon(mapBounds)
  },
  lru: DEFAULT_LRU_SIZE,
})

// turns the google maps bounding box into a geoJSON multipolygon
// to make calling APIs easier
export const mapInfoPolygon = uncachedSelector<MapInfoPolygon>({
  key: key('map-info-polygon'),
  get: ({ get }) => {
    const mapBounds = get(bounds)
    const polygon = toMultiPolygon(mapBounds)
    return { polygon }
  },
  lru: DEFAULT_LRU_SIZE,
})

export const center = atom<Position | undefined>({
  key: key('map-position'),
  default: austin.center,
})

export enum H3Level {
  XSMALL = 10,
  SMALL = 8,
  MEDIUM = 6,
  LARGE = 4,
  XLARGE = 2,
}

export const h3Level = uncachedSelector<number>({
  key: key('h3-level'),
  get: ({ get }) => {
    const zoomLevel = get(zoom)
    if (zoomLevel < 10) {
      return 2
    }
    return Math.min(zoomLevel - 5, 10)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const activeLayers = atom<MainMapLayers[]>({
  key: key('active-layers'),
  default: ['pins'],
})

export const mapStyle = atom<MapStyle>({
  key: key('map-style'),
  default: MAP_DEFAULT_STYLE,
  effects: [localStorageEffect('map-style')],
})
